import SectionHeading from "@/pages/home/components/SectionHeading";

const tiers = [
  {
    id: "t4k",
    name: "蓝光 4K",
    res: "约 3840 × 2160",
    desc: "画质天花板，暗部层次与细节最完整。适合投屏到大电视或在显示器上慢慢看，对带宽和存储空间要求也最高。",
    hint: "推荐：大屏观影、画质党",
    tone: "accent",
  },
  {
    id: "t1080",
    name: "1080P 高清",
    res: "约 1920 × 1080",
    desc: "主流高清档，清晰度与体积平衡得最好。绝大多数剧集与老片修复版都集中在这个档位，日常观看性价比最高。",
    hint: "推荐：日常追剧、手机看",
    tone: "primary",
  },
  {
    id: "thd",
    name: "HD 高清",
    res: "720P 及以上",
    desc: "体积小、加载快，弱网环境也能顺畅播放。适合通勤路上用手机看，或者在空间紧张时先存一份。",
    hint: "推荐：弱网、临时观看",
    tone: "secondary",
  },
];

const toneMap: Record<string, string> = {
  accent: "bg-accent-100 text-accent-800",
  primary: "bg-primary-100 text-primary-700",
  secondary: "bg-secondary-100 text-secondary-900",
};

export default function BluraySection() {
  return (
    <section id="bluray" className="scroll-mt-20 bg-background-50">
      <div className="mx-auto w-full max-w-6xl px-4 py-16 md:px-6 md:py-20">
        <SectionHeading
          eyebrow="蓝光画质专区"
          title="先搞清楚画质档位，再决定存哪一版"
          desc="同一部作品往往有多个画质版本，选错要么占空间要么看不清。这里把三档画质讲清楚，并说明各自适合的观看场景，方便你按设备而不是按数字去挑。"
        />

        <div className="grid grid-cols-1 gap-4 md:gap-5 lg:grid-cols-3">
          {tiers.map((tier) => (
            <article
              key={tier.id}
              className="flex flex-col gap-3 rounded-lg border border-background-200/70 bg-background-100/60 p-5"
            >
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-heading text-lg font-semibold text-foreground-950">{tier.name}</h3>
                <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${toneMap[tier.tone]}`}>
                  {tier.res}
                </span>
              </div>
              <p className="text-sm leading-relaxed text-foreground-700">{tier.desc}</p>
              <p className="mt-auto border-t border-background-200/70 pt-3 text-xs text-foreground-600">
                {tier.hint}
              </p>
            </article>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 items-stretch gap-5 lg:grid-cols-[1.2fr_1fr]">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <figure className="overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
              <div className="h-44 w-full overflow-hidden md:h-52">
                <img
                  src="https://readdy.ai/api/search-image?query=Stylized%20illustration%20of%20a%204K%20ultra%20HD%20home%20cinema%20screen%20glowing%20in%20a%20dark%20cozy%20room%2C%20warm%20amber%20ambient%20light%2C%20crisp%20geometric%20shapes%2C%20minimalist%20artistic%20composition%2C%20high%20detail%2C%20no%20text&width=960&height=640&seq=blu-4k-zone&orientation=landscape"
                  alt="4K 超高清家庭影音场景示意"
                  title="蓝光 4K 画质观影场景"
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <figcaption className="px-4 py-3 text-sm text-foreground-700">
                4K 片源在 55 英寸以上屏幕才能明显体现细节优势。
              </figcaption>
            </figure>
            <figure className="overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
              <div className="h-44 w-full overflow-hidden md:h-52">
                <img
                  src="https://readdy.ai/api/search-image?query=Stylized%20illustration%20of%20a%20Blu-ray%20disc%20and%20sleek%20player%20on%20a%20warm%20wooden%20desk%2C%20golden%20soft%20light%2C%20minimalist%20clean%20composition%2C%20amber%20and%20green%20tones%2C%20high%20detail%2C%20no%20text&width=960&height=640&seq=blu-1080-zone&orientation=landscape"
                  alt="蓝光光盘与播放设备示意"
                  title="1080P 高清片源参考"
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <figcaption className="px-4 py-3 text-sm text-foreground-700">
                1080P 在手机与笔记本上几乎看不出与 4K 的差距。
              </figcaption>
            </figure>
          </div>

          <div className="flex flex-col justify-center gap-4 rounded-lg border border-background-200/70 bg-background-100/60 p-5 md:p-6">
            <h3 className="font-heading text-lg font-semibold text-foreground-950">挑画质前的三个自检</h3>
            <ul className="flex flex-col gap-3">
              {[
                "设备屏幕分辨率是否高于 1080P，否则 4K 优势基本体现不出来。",
                "网络带宽是否稳定，超大体积片源在弱网下反而更容易卡顿。",
                "是否打算长期收藏，收藏建议选最高档，临时观看选中档即可。",
              ].map((tip) => (
                <li key={tip} className="flex gap-3 text-sm leading-relaxed text-foreground-700">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
            <p className="rounded-md bg-secondary-50 px-4 py-3 text-xs leading-relaxed text-secondary-900">
              画质标注只是参考档位，实际观感还会受片源压制方式、播放器解码能力和屏幕素质影响。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}